import { getFunctions, httpsCallable } from "firebase/functions";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";

/**
 * Service for managing Firebase Auth user creation
 */

/**
 * Create Auth user for a specific Firestore user
 * @param {string} userId - Firestore user document ID
 * @returns {Promise<Object>} Result with success status and message
 */
export async function createAuthUserManually(userId) {
  try {
    const functions = getFunctions();
    const createAuthUser = httpsCallable(functions, "createAuthUserManually");

    console.log(`🔄 Calling function to create Auth user for: ${userId}`);

    const result = await createAuthUser({ userId });

    console.log(`✅ Function response:`, result.data);

    return result.data;
  } catch (error) {
    console.error(`❌ Failed to create Auth user:`, error);

    // Parse Firebase error
    if (error.code === "functions/permission-denied") {
      throw new Error("You don't have permission to create Auth users. Admin or Broker access required.");
    } else if (error.code === "functions/not-found") {
      throw new Error("User not found in Firestore.");
    } else if (error.code === "functions/unauthenticated") {
      throw new Error("You must be logged in to perform this action.");
    } else {
      throw new Error(error.message || "Failed to create Auth user");
    }
  }
}

/**
 * Find all users missing Auth accounts
 * @returns {Promise<Array>} Array of users without authCreated flag
 */
export async function findUsersMissingAuth() {
  try {
    const usersRef = collection(db, "users");

    // Query users where authCreated is false or doesn't exist
    const usersSnapshot = await getDocs(usersRef);

    const missingAuth = [];
    usersSnapshot.forEach((doc) => {
      const data = doc.data();
      if (!data.authCreated && data.email) {
        missingAuth.push({
          id: doc.id,
          email: data.email,
          name: data.name,
          plan: data.plan,
          invitedAt: data.invitedAt,
        });
      }
    });

    console.log(`📊 Found ${missingAuth.length} users without Auth accounts`);

    return missingAuth;
  } catch (error) {
    console.error("Failed to find users missing Auth:", error);
    throw error;
  }
}

/**
 * Create Auth accounts for multiple users in batch
 * @param {Array<string>} userIds - Array of user IDs
 * @param {Function} progressCallback - Optional callback for progress updates
 * @returns {Promise<Object>} Results summary
 */
export async function createAuthUsersBatch(userIds, progressCallback) {
  const results = {
    total: userIds.length,
    success: 0,
    failed: 0,
    errors: [],
  };

  for (let i = 0; i < userIds.length; i++) {
    const userId = userIds[i];

    try {
      const result = await createAuthUserManually(userId);

      if (result.success) {
        results.success++;
        console.log(`✅ [${i + 1}/${userIds.length}] Success: ${result.email}`);
      } else {
        results.failed++;
        results.errors.push({ userId, error: result.message });
        console.log(`⏭️  [${i + 1}/${userIds.length}] Skipped: ${result.message}`);
      }

      if (progressCallback) {
        progressCallback({
          current: i + 1,
          total: userIds.length,
          userId,
          success: results.success,
          failed: results.failed,
        });
      }

      // Rate limiting: wait 500ms between requests
      await new Promise((resolve) => setTimeout(resolve, 500));
    } catch (error) {
      results.failed++;
      results.errors.push({ userId, error: error.message });
      console.error(`❌ [${i + 1}/${userIds.length}] Failed:`, error.message);

      if (progressCallback) {
        progressCallback({
          current: i + 1,
          total: userIds.length,
          userId,
          success: results.success,
          failed: results.failed,
          error: error.message,
        });
      }
    }
  }

  return results;
}

// Make functions available globally in browser console for debugging
if (typeof window !== "undefined") {
  window.createAuthUserManually = createAuthUserManually;
  window.findUsersMissingAuth = findUsersMissingAuth;
  window.createAuthUsersBatch = createAuthUsersBatch;

  console.log("🔐 Auth User Service loaded!");
  console.log("   → createAuthUserManually(userId)");
  console.log("   → findUsersMissingAuth()");
  console.log("   → createAuthUsersBatch([userIds])");
}

